// @flow

import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;

  > div {
    min-height: 0;
  }
`;

const Divider = styled.div`
  /* Use an odd height so a 1px center line can be placed exactly on a device pixel */
  height: 11px;
  width: 99%;
  margin: 3px auto;
  box-sizing: border-box;
  cursor: row-resize;
  position: relative;
  /* Background only; line drawn via pseudo-element to match native dashed border rhythm */
  background: var(--main-background);
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  /* Icon + gap sizing */
  --icon-size: 14px; /* default; can be overridden inline */
  --line-gap: calc((var(--icon-size) / 2) + 10px);

  /* Ensure the icon (and its wrapper) sits above the line and doesn't intercept events */
  > * {
    position: relative;
    z-index: 2;
    pointer-events: none;
    display: block;
  }

  /* Two segments for the horizontal line, leaving a gap around the icon */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 0.5px); /* crisp 1px placement */
    height: 0;
    border-top: 1px solid var(--mid-grey);
    z-index: 1;
    pointer-events: none;
  }

  /* Left segment: from left edge to (center - line-gap) */
  &::before {
    left: 0;
    right: calc(50% + var(--line-gap));
  }

  /* Right segment: from (center + line-gap) to right edge */
  &::after {
    left: calc(50% + var(--line-gap));
    right: 0;
  }
`;

/**
 * A vertical resizable split layout with a draggable divider.
 * Props:
 * - initialSplit: number (0..1) initial proportion for top pane
 * - onSplitChange?: (n) => void  callback when split changes
 * - min?: number  minimum top proportion (default 0.15)
 * - max?: number  maximum top proportion (default 0.85)
 * - top: React.Node top content (should be a flex item)
 * - bottom: React.Node bottom content (should be a flex item)
 * - contentRef?: any forward ref for outer content area (optional)
 * - dividerRef?: any forward ref for divider (optional)
 * - collapsed?: boolean when true, bottom panel shows only minimal height
 * - minBottomHeight?: number minimum height when collapsed (default: 48px for header)
 */
export default function ResizableSplit({
  initialSplit = 0.5,
  onSplitChange,
  min = 0.15,
  max = 0.85,
  top,
  bottom,
  contentRef,
  dividerRef,
  collapsed = false,
  minBottomHeight = 48,
  hideDividerWhenCollapsed = false,
}) {
  const [split, setSplit] = useState(initialSplit);
  const isDraggingRef = useRef(false);
  const localContentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const topFlex = useMemo(() => Math.max(min, Math.min(max, split)), [split, min, max]);
  const bottomFlex = useMemo(() => 1 - topFlex, [topFlex]);
  const shouldHideBottom = collapsed && hideDividerWhenCollapsed;
  const effectiveTopFlex = shouldHideBottom ? 1 : topFlex;

  // Keep external consumer informed
  useEffect(() => {
    if (typeof onSplitChange === 'function') onSplitChange(topFlex);
    // only when derived topFlex changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topFlex]);

  // Drag handling
  useEffect(() => {
    function onMove(e) {
      if (!isDraggingRef.current) return;
      const el = (contentRef && contentRef.current) || localContentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const next = Math.min(max, Math.max(min, y / rect.height));
      setSplit(next);
    }
    function onUp() {
      isDraggingRef.current = false;
      setIsDragging(false);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [min, max, contentRef]);

  const transition = isDragging ? 'none' : 'flex 0.3s ease';

  return (
    <ContentArea ref={contentRef || localContentRef}>
      <div
        style={{
          flex: shouldHideBottom ? '1 1 0px' : `${topFlex} 1 0px`,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          transition,
        }}
      >
        {top}
      </div>

      {hideDividerWhenCollapsed ? (
        <Divider
          ref={dividerRef}
          role="separator"
          aria-orientation="horizontal"
          onMouseDown={() => {
            if (!collapsed) {
              isDraggingRef.current = true;
              setIsDragging(true);
            }
          }}
          style={{
            '--icon-size': '14px',
            cursor: collapsed ? 'default' : 'row-resize',
            opacity: shouldHideBottom ? 0 : collapsed ? 0.5 : 1,
            height: shouldHideBottom ? 0 : undefined,
            margin: shouldHideBottom ? 0 : undefined,
            overflow: 'hidden',
            transition: isDragging ? 'none' : 'opacity 0.3s ease, height 0.3s ease, margin 0.3s ease',
          }}
        >
          <Icon icon="drag-handle-horizontal" size={14} color="var(--mid-grey)" />
        </Divider>
      ) : (
        <Divider
          ref={dividerRef}
          role="separator"
          aria-orientation="horizontal"
          onMouseDown={() => {
            if (!collapsed) {
              isDraggingRef.current = true;
              setIsDragging(true);
            }
          }}
          style={{
            '--icon-size': '14px',
            cursor: collapsed ? 'default' : 'row-resize',
            opacity: collapsed ? 0.5 : 1,
          }}
        >
          <Icon icon="drag-handle-horizontal" size={14} color="var(--mid-grey)" />
        </Divider>
      )}

      <div
        style={{
          flex: shouldHideBottom ? '0 0 0px' : collapsed
            ? '0 0 auto'
            : `${bottomFlex} 1 0px`,
          height: shouldHideBottom ? 0 : collapsed ? minBottomHeight : undefined,
          minHeight: shouldHideBottom ? 0 : collapsed ? undefined : 0,
          overflow: 'hidden',
          display: 'flex',
          transition,
        }}
      >
        {bottom}
      </div>
    </ContentArea>
  );
}
